// Private share link for an article awaiting approval (/blog/preview/<token>).
// The token is the only credential — nothing here is linked, indexed, listed
// on /blog, in sitemap.xml or in /ai/articles.json, and no structured data is
// emitted, so a preview can never surface in search or become a second URL for
// live content. Published articles deliberately 404 here.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Clock, EyeOff } from "lucide-react";
import BlockRenderer from "@/components/blog/BlockRenderer";
import { getBlogPreview } from "@/lib/blog-cms.functions";
import type { PublicPost } from "@/lib/blog/types";
import { publicAuthorName } from "@/lib/blog/author";

type PreviewData = { post: PublicPost; status: string; slug: string };

const STATUS_COPY: Record<string, string> = {
  draft: "Draft, not submitted for approval yet.",
  in_review: "Awaiting admin approval. This is exactly how the article will look once published.",
  archived: "Archived, removed from the public site.",
};

export const Route = createFileRoute("/blog/preview/$token")({
  loader: async ({ params }) => {
    const data = await getBlogPreview({ data: { token: params.token } }).catch(
      () => null,
    );
    if (!data) throw notFound();
    return data as PreviewData;
  },
  // Preview URLs are noindex/nofollow and carry no canonical, og or JSON-LD.
  head: () => ({
    meta: [
      { title: "Article preview | TruWe" },
      { name: "robots", content: "noindex, nofollow, noarchive" },
      { name: "googlebot", content: "noindex, nofollow" },
    ],
  }),
  errorComponent: () => <PreviewMissing />,
  notFoundComponent: () => <PreviewMissing />,
  component: PreviewArticle,
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background pb-24 pt-28 md:pt-36">
      <article className="mx-auto max-w-3xl px-4 md:px-8">{children}</article>
    </div>
  );
}

function PreviewMissing() {
  return (
    <Shell>
      <h1 className="font-display text-2xl font-bold text-brand-deep">
        This preview link isn’t valid
      </h1>
      <p className="mt-3 text-muted-foreground">
        The link may have expired, or the article is already live. Browse the{" "}
        <Link to="/blog" className="text-brand underline">
          Clinical Library
        </Link>
        .
      </p>
    </Shell>
  );
}

function PreviewArticle() {
  const { post, status } = Route.useLoaderData() as PreviewData;

  return (
    <Shell>
      <div className="mb-8 flex items-start gap-3 rounded-2xl border border-brand/30 bg-brand/5 p-4">
        <EyeOff className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        <div className="text-sm">
          <p className="font-semibold text-brand-deep">Private preview</p>
          <p className="mt-1 text-muted-foreground">
            {STATUS_COPY[status] ?? "This article is not published."} Anyone with this
            link can read it, but it is hidden from the blog, the sitemap and search
            engines.
          </p>
        </div>
      </div>

      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
        {post.category}
      </p>
      <h1 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
        {post.title}
      </h1>
      {post.excerpt ? (
        <p className="mt-4 text-base text-muted-foreground md:text-lg">{post.excerpt}</p>
      ) : null}
      <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" /> {post.readingTime}
        </span>
        <span aria-hidden>·</span>
        <span>{publicAuthorName(post.authorName)}</span>
        {post.reviewerName ? (
          <>
            <span aria-hidden>·</span>
            <span>
              Medically reviewed by {post.reviewerName}
              {post.reviewerCredentials ? `, ${post.reviewerCredentials}` : ""}
            </span>
          </>
        ) : null}
      </div>

      {post.heroImageUrl ? (
        <img
          src={post.heroImageUrl}
          alt={post.heroImageAlt ?? post.title}
          className="mt-8 w-full rounded-3xl border border-border object-cover"
        />
      ) : null}

      {post.keyTakeaways.length ? (
        <section className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-base font-bold text-brand-deep">
            Key takeaways
          </h2>
          <ul className="mt-3 space-y-2">
            {post.keyTakeaways.map((t, i) => (
              <li key={i} className="flex gap-2 text-sm leading-relaxed text-muted-foreground">
                <span aria-hidden className="text-brand">
                  •
                </span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <BlockRenderer blocks={post.body} />

      {post.faqs.length ? (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-brand-deep md:text-3xl">
            Frequently asked questions
          </h2>
          <div className="mt-4 space-y-3">
            {post.faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-display text-base font-bold text-brand-deep">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {post.references.length ? (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-brand-deep md:text-3xl">
            References
          </h2>
          <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
            {post.references.map((r, i) => (
              <li key={i}>
                <a href={r.url} target="_blank" rel="noopener nofollow" className="underline">
                  {r.label}
                </a>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <footer className="mt-14 rounded-2xl border border-border bg-card p-5 text-xs text-muted-foreground">
        Educational content only, not medical advice.{" "}
        <Link to="/blog" className="inline-flex items-center gap-1 underline">
          <ArrowLeft className="h-3 w-3" /> Clinical Library
        </Link>
      </footer>
    </Shell>
  );
}
